import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query, orderBy, where, getDocs, doc, updateDoc, increment, addDoc, serverTimestamp } from 'firebase/firestore';
import { deleteToRecycleBin } from './recycleBinUtil';
import { isCourierCod } from './dueUtil';
import { printSalesInvoice } from './printInvoice';

const getTodayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const Contacts = () => {
  const [customers, setCustomers] = useState([]);
  const [dueSales, setDueSales] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeDatePreset, setActiveDatePreset] = useState(null);
  const [searchQuery, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [receiveInput, setReceiveInput] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);

  const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.ceil(num || 0));

  // তারিখ ফরম্যাট করার নিরাপদ ফাংশন (DD/MM/YYYY)
  const formatDateSafe = (timestamp) => {
    if (!timestamp) return "N/A";
    const d = new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
    return d.toLocaleDateString('en-GB'); // দিন/মাস/বছর ফরম্যাট
  };

  useEffect(() => {
    // ---- সব কাস্টমার (শুধু "আজকের" নয়) — customers কালেকশন থেকে, Sales.js নতুন ফোন নম্বর
    // পেলেই এখানে যোগ করে। আগে এই পেজ শুধু "আজকের" sales থেকে কাস্টমার বের করত, তাই
    // দিন পাল্টালেই আগের কাস্টমাররা পুরোপুরি হারিয়ে যেত। ----
    const unsubCustomers = onSnapshot(query(collection(db, "customers"), orderBy("createdAt", "desc")), (snap) => {
      setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    // ---- বাকি থাকা ডিউ (সব সময়ের) — শুধু dueAmount > 0 এমন sale-গুলোই আনা হচ্ছে,
    // পুরো sales history পড়ার দরকার নেই ----
    const unsubDue = onSnapshot(query(collection(db, "sales"), where("dueAmount", ">", 0)), (snap) => {
      setDueSales(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubCustomers(); unsubDue(); };
  }, []);

  // ---- প্রতিটা ফোন নম্বরের বিপরীতে মোট বাকি টাকা ----
  // কুরিয়ার (COD) সেলগুলো বাদ — ওই টাকা কাস্টমার বাকি রাখেনি, ওটা কুরিয়ারের কাছে।
  // নইলে যে কাস্টমার কুরিয়ারে অর্ডার করেছে তাকেও এখানে বাকিদার হিসেবে দেখাবে।
  const dueByPhone = useMemo(() => {
    const map = new Map();
    dueSales.filter(s => !isCourierCod(s)).forEach(s => {
      const due = Number(s.dueAmount || 0);
      [s.phone, s.phone2, s.customerPhone].filter(Boolean).forEach(p => {
        map.set(p, (map.get(p) || 0) + due);
      });
    });
    return map;
  }, [dueSales]);

  // "Since" তারিখ (createdAt) অনুযায়ী কাস্টমার ফিল্টার করার জন্য YYYY-MM-DD আকারে বের করা
  const customerDateKey = (c) => {
    const raw = c.createdAt;
    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const applyDatePreset = (mode) => {
    const today = new Date();
    if (mode === 'today') {
      const t = getTodayString();
      setStartDate(t); setEndDate(t);
    } else if (mode === 'month') {
      setStartDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
      setEndDate(getTodayString());
    }
    setActiveDatePreset(mode);
  };

  const clearDateFilter = () => {
    setStartDate(''); setEndDate(''); setActiveDatePreset(null);
  };

  const filteredCustomerList = useMemo(() => {
    const term = customerFilter.trim().toLowerCase();
    return customers.filter(c => {
      const matchesTerm = !term ||
        (c.name || '').toLowerCase().includes(term) ||
        (c.phone || '').includes(term) ||
        (c.phone2 || '').includes(term);
      if (!matchesTerm) return false;
      if (!startDate && !endDate) return true;
      const key = customerDateKey(c);
      if (!key) return false;
      return (!startDate || key >= startDate) && (!endDate || key <= endDate);
    });
  }, [customers, customerFilter, startDate, endDate]);

  // ---- একজন কাস্টমারের (এই ফোন নম্বরের) সব সময়ের সেল হিস্ট্রি লোড করা —
  // সার্চ বক্স এবং কাস্টমার কার্ডের "View History" বাটন দুটোই এটা ব্যবহার করে ----
  const loadCustomerHistory = async (phone, { silent } = {}) => {
    if (!phone) return;
    const results = new Map();
    for (const field of ["phone", "phone2", "customerPhone"]) {
      const snap = await getDocs(query(collection(db, "sales"), where(field, "==", phone)));
      snap.docs.forEach(d => results.set(d.id, { id: d.id, ...d.data() }));
    }
    const sorted = Array.from(results.values()).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setSearchResults(sorted);
    if (sorted.length === 0 && !silent) alert("Customer not found!");
  };

  const handleSearch = () => {
    if (!searchQuery) return;
    loadCustomerHistory(searchQuery.trim());
  };

  const loadPaymentHistory = async (saleId) => {
    try {
      const snap = await getDocs(query(collection(db, "duePayments"), where("saleId", "==", saleId)));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setPaymentHistory(list);
    } catch (e) {
      setPaymentHistory([]);
    }
  };

  const openInvoiceView = (sale) => {
    setViewInvoice(sale);
    setReceiveInput(String(Number(sale.dueAmount || 0)));
    setPaymentHistory([]);
    loadPaymentHistory(sale.id);
  };

  const submitReceive = async () => {
    if (!viewInvoice) return;
    const due = Number(viewInvoice.dueAmount || 0);
    if (due <= 0) return;
    const amt = Math.min(due, Math.max(0, Number(receiveInput) || 0));
    if (amt <= 0) return alert("Invalid amount!");
    try {
      await updateDoc(doc(db, "sales", viewInvoice.id), { paid: increment(amt), dueAmount: increment(-amt) });
      await addDoc(collection(db, "duePayments"), {
        invoiceId: viewInvoice.invoiceId,
        saleId: viewInvoice.id,
        customerName: viewInvoice.name || viewInvoice.customerName || '',
        customerPhone: [viewInvoice.phone, viewInvoice.phone2].filter(Boolean).join(', ') || viewInvoice.customerPhone || '',
        amount: amt,
        createdAt: serverTimestamp()
      });
      const updated = { ...viewInvoice, paid: (Number(viewInvoice.paid) || 0) + amt, dueAmount: Math.max(0, due - amt) };
      setViewInvoice(updated);
      setReceiveInput(String(updated.dueAmount));
      setSearchResults(prev => prev.map(s => s.id === updated.id ? updated : s));
      setPaymentHistory(prev => [...prev, { amount: amt, createdAt: new Date() }]);
      alert(`৳ ${formatTk(amt)} received!`);
    } catch (e) {
      alert("Error receiving payment: " + e.message);
    }
  };

  const handleReturn = async (sale, itemIdx) => {
    const item = sale.items[itemIdx];
    const qty = Number(window.prompt(`Return Qty for ${item.name}? (Max: ${item.qty})`, "1"));
    if (!qty || qty > item.qty) return alert("Invalid Qty!");
    const reason = window.prompt("Reason for return?");
    try {
      const discountRatio = (sale.discount || 0) / (sale.subtotal || 1);
      const refund = Math.ceil((item.price - (item.price * discountRatio)) * qty);
      await updateDoc(doc(db, "products", item.id), { stock: increment(qty) });
      await addDoc(collection(db, "returns"), { invoiceId: sale.invoiceId, name: item.name, qty, refund, reason, date: serverTimestamp() });
      const newItems = [...sale.items];
      if (newItems[itemIdx].qty === qty) newItems.splice(itemIdx, 1);
      else newItems[itemIdx].qty -= qty;
      if (newItems.length === 0) await deleteToRecycleBin({ collectionName: 'sales', id: sale.id, data: sale, label: 'Sale', reason: 'All items returned' });
      else await updateDoc(doc(db, "sales", sale.id), { items: newItems, totalBill: increment(-refund) });
      alert("Returned successfully!");
      setSelectedInvoice(null);
    } catch (e) { alert("Error!"); }
  };

  return (
    <div className="fade-in">
      <div className="white-box" style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'20px' }}>
        <div style={{ flex: 1 }}>
          <h3>🔍 Search Customer</h3>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <input value={searchQuery} onChange={e => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} placeholder="Enter Phone Number..." className="input-field" style={{ marginBottom: 0 }} />
            <button onClick={handleSearch} className="btn-confirm" style={{ padding: '0 20px', borderRadius: '15px' }}>Search</button>
          </div>
        </div>
      </div>

      <div className="white-box" style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
          <h3 style={{ color: '#4a00e0', margin: 0 }}>👥 All Customers ({filteredCustomerList.length}/{customers.length})</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {[{ key: 'today', label: 'Today' }, { key: 'month', label: 'This Month' }].map(p => (
              <button
                key={p.key}
                onClick={() => applyDatePreset(p.key)}
                style={{
                  padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
                  border: `1px solid ${activeDatePreset === p.key ? '#4a00e0' : '#ddd'}`,
                  background: activeDatePreset === p.key ? '#4a00e0' : '#fff',
                  color: activeDatePreset === p.key ? '#fff' : '#333'
                }}
              >
                {p.label}
              </button>
            ))}
            <input type="date" className="input-field" value={startDate} onChange={e => { setStartDate(e.target.value); setActiveDatePreset(null); }} style={{ width: '150px', marginBottom: 0 }} />
            <input type="date" className="input-field" value={endDate} onChange={e => { setEndDate(e.target.value); setActiveDatePreset(null); }} style={{ width: '150px', marginBottom: 0 }} />
            {(startDate || endDate) && (
              <button onClick={clearDateFilter} style={{ padding: '8px 12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>Clear</button>
            )}
            <input
              value={customerFilter}
              onChange={e => setCustomerFilter(e.target.value)}
              placeholder="Filter by name or phone..."
              className="input-field"
              style={{ maxWidth: '250px', marginBottom: 0 }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', maxHeight: '420px', overflowY: 'auto', paddingBottom: '10px' }}>
           {filteredCustomerList.length === 0 && (
             <p style={{ color: '#999', fontSize: '13px', padding: '10px 0' }}>No customers found.</p>
           )}
           {filteredCustomerList.map((c, i) => {
             const due = dueByPhone.get(c.phone) || dueByPhone.get(c.phone2) || 0;
             return (
               <div className="hover-lift" key={c.id || i} style={{ minWidth: '220px', background: '#f8f9ff', padding: '15px', borderRadius: '20px', border: '1px solid #eee' }}>
                 <b>{c.name || "Walk-in"}</b>
                 <p style={{ margin: '5px 0', fontSize: '12px', color: '#888' }}>{[c.phone, c.phone2].filter(Boolean).join(', ')}</p>
                 <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: '#aaa' }}>Since: {formatDateSafe(c.createdAt)}</p>
                 {due > 0 ? (
                   <div style={{ fontSize: '12px', color: '#e74c3c', fontWeight: 'bold', marginBottom: '8px' }}>Due: ৳ {formatTk(due)}</div>
                 ) : (
                   <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold', marginBottom: '8px' }}>No Due</div>
                 )}
                 <button onClick={() => loadCustomerHistory(c.phone || c.phone2, { silent: true })} style={{ width: '100%', fontSize: '12px', padding: '7px 0', background: '#4a00e0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>View History</button>
               </div>
             );
           })}
        </div>
      </div>

      {/* কাস্টমারের হিস্ট্রি এখন সরাসরি একটা পপআপে দেখায় — আগে পেজের নিচে দেখাত, স্ক্রল করে দেখতে হতো */}
      {searchResults.length > 0 && (
        <div onClick={() => setSearchResults([])} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} className="white-box fade-in" style={{ width: '600px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setSearchResults([])} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', zIndex: 10 }}>X</button>
            {(() => {
              const profile = searchResults[0];
              // কাস্টমারের নিজের বাকি আর কুরিয়ারে থাকা COD আলাদা করে দেখানো হচ্ছে
              const totalDue = searchResults.filter(s => !isCourierCod(s)).reduce((sum, s) => sum + Number(s.dueAmount || 0), 0);
              const totalCod = searchResults.filter(isCourierCod).reduce((sum, s) => sum + Number(s.dueAmount || 0), 0);
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '15px', paddingRight: '40px' }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{profile.name || profile.customerName || 'Walk-in'}</h3>
                    <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '13px' }}>
                      {[profile.phone, profile.phone2].filter(Boolean).join(', ') || profile.customerPhone}
                      {profile.address ? ` • ${profile.address}` : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {totalDue > 0 && (
                      <div style={{ background: '#fff5f5', border: '1px solid #ffdede', borderRadius: '10px', padding: '8px 14px', textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#888' }}>Total Due</div>
                        <div style={{ fontWeight: 'bold', color: '#e74c3c', fontSize: '16px' }}>৳ {formatTk(totalDue)}</div>
                      </div>
                    )}
                    {totalCod > 0 && (
                      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '8px 14px', textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#888' }}>In Courier (COD)</div>
                        <div style={{ fontWeight: 'bold', color: '#1e40af', fontSize: '16px' }}>৳ {formatTk(totalCod)}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            <table style={{ width: '100%' }}>
               <thead><tr style={{ textAlign: 'left', color: '#888' }}><th>Date</th><th>Invoice</th><th>Bill</th><th>Due</th><th></th></tr></thead>
               <tbody>
                  {searchResults.map((s, i) => (
                    <tr key={i} style={{borderBottom:'1px solid #eee'}}>
                      <td style={{ padding: '12px' }}>{formatDateSafe(s.createdAt)}</td>
                      <td onClick={()=>setSelectedInvoice(s)} style={{color:'#4a00e0', fontWeight:'bold', cursor:'pointer'}}>#{s.invoiceId}</td>
                      <td style={{ fontWeight: 'bold' }}>৳ {formatTk(s.totalBill)}</td>
                      <td style={{ fontWeight: 'bold', color: isCourierCod(s) ? '#1e40af' : (s.dueAmount > 0 ? '#e74c3c' : '#888') }}>
                        {s.dueAmount > 0 ? (isCourierCod(s) ? `COD ৳ ${formatTk(s.dueAmount)}` : `৳ ${formatTk(s.dueAmount)}`) : '-'}
                      </td>
                      <td align="right">
                        <button onClick={() => openInvoiceView(s)} style={{ fontSize: '11px', padding: '5px 10px', background: '#4a00e0', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>View Invoice</button>
                      </td>
                    </tr>
                  ))}
               </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedInvoice && (
        <div className="modal-overlay" onClick={()=>setSelectedInvoice(null)}>
          <div className="white-box" onClick={e=>e.stopPropagation()} style={{width:'500px'}}>
            <h3>Invoice #{selectedInvoice.invoiceId} <button onClick={()=>setSelectedInvoice(null)} style={{float:'right', border:'none', color:'red'}}>X</button></h3>
            <hr/>
            <table style={{width:'100%', marginTop:'10px'}}>
              {selectedInvoice.items.map((it, idx) => (
                <tr key={idx}>
                  <td style={{padding:'8px'}}>{it.name} x {it.qty}</td>
                  <td align='right'>৳ {formatTk(it.price * it.qty)}</td>
                  <td align='right'><button onClick={()=>handleReturn(selectedInvoice, idx)} style={{color:'red', border:'none', background:'none', cursor:'pointer'}}>Return</button></td>
                </tr>
              ))}
            </table>
            <h3 style={{textAlign:'right', marginTop:'15px'}}>Total: ৳ {formatTk(selectedInvoice.totalBill)}</h3>
          </div>
        </div>
      )}

      {viewInvoice && (
        <div className="modal-overlay" onClick={() => setViewInvoice(null)} style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000, padding:'20px', overflowY:'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', padding:'30px', borderRadius:'25px', width:'450px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)', position:'relative', maxHeight:'95vh', overflowY:'auto' }}>
            <button onClick={() => setViewInvoice(null)} style={{ position:'absolute', top:'15px', right:'15px', border:'none', background:'#ffeded', color:'red', width:'30px', height:'30px', borderRadius:'50%', cursor:'pointer', fontWeight:'bold', zIndex:10 }}>X</button>

            <div style={{ textAlign:'center', marginBottom:'15px' }}>
              <h1 style={{ margin:0, color:'#4a00e0', fontSize:'22px' }}>PB KIZUNA COMPANY LIMITED</h1>
              <p style={{ margin:'4px 0', fontSize:'11px', fontWeight:'bold' }}>H-10, R-2, Section-2, Block-D, <br/>Aftabnagar, Dhaka-1212</p>
              <p style={{ margin:0, fontSize:'11px' }}>Mobile: 01884-711844</p>
              <div style={{ borderBottom:'1px dashed #000', margin:'12px 0' }}></div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', letterSpacing: '1px' }}>SALES INVOICE</h4>
            </div>

            <div style={{ display:'flex', gap:'10px', fontSize:'12px', marginBottom:'12px' }}>
              <div style={{ flex:1 }}>
                <p style={{ margin: '2px 0' }}><b>Sold By:</b> {viewInvoice.soldBy}</p>
                <p style={{ margin: '2px 0' }}><b>Date:</b> {viewInvoice.dateTime || formatDateSafe(viewInvoice.createdAt)}</p>
                <p style={{ margin: '2px 0' }}><b>Invoice ID:</b> #{viewInvoice.invoiceId}</p>
              </div>
              <div style={{ flex:1, border:'1px solid #ddd', borderRadius:'8px', padding:'8px 10px', background:'#f8f9ff' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight:'bold', color:'#4a00e0' }}>Bill To</p>
                <p style={{ margin: '2px 0' }}>{viewInvoice.name || viewInvoice.customerName || 'Walk-in Customer'}</p>
                {[viewInvoice.phone, viewInvoice.phone2].filter(Boolean).length > 0 && (
                  <p style={{ margin: '2px 0' }}>{[viewInvoice.phone, viewInvoice.phone2].filter(Boolean).join(', ')}</p>
                )}
                {viewInvoice.address && <p style={{ margin: '2px 0' }}>{viewInvoice.address}</p>}
              </div>
            </div>
            <div style={{ borderBottom:'1px dashed #000', margin:'8px 0 12px' }}></div>

            <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #eee' }}>
                  <th align='left' style={{ paddingBottom: '4px' }}>ITEM</th>
                  <th align='center' style={{ paddingBottom: '4px' }}>QTY</th>
                  <th align='right' style={{ paddingBottom: '4px' }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {(viewInvoice.items || []).map((it, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{padding:'5px 0'}}>
                      <b>{it.name}</b>
                      {it.itemDiscAmount > 0 && (
                        <div style={{fontSize:'10px', color:'#e74c3c'}}>Disc: -৳ {formatTk(it.itemDiscAmount)}</div>
                      )}
                    </td>
                    <td align='center'>{it.qty}</td>
                    <td align='right'>{formatTk(it.finalItemTotal ?? (it.price * it.qty))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ borderTop:'1px solid #eee', marginTop:'10px', paddingTop:'8px', textAlign:'right', fontSize:'12px' }}>
              {viewInvoice.subtotal > 0 && <p style={{ margin: '3px 0' }}>Sub Total: ৳ {formatTk(viewInvoice.subtotal)}</p>}
              {viewInvoice.totalDiscount > 0 && (
                <p style={{ margin: '3px 0', color:'#e74c3c' }}>Total Discount: -৳ {formatTk(viewInvoice.totalDiscount)}</p>
              )}
              {viewInvoice.deliveryCharge > 0 && (
                viewInvoice.deliveryPaidBy === 'shop' ? (
                  <p style={{ margin: '3px 0', color: '#c0392b' }}>Delivery Charge: ৳ {formatTk(viewInvoice.deliveryCharge)} (paid by {viewInvoice.deliveryPaidByName})</p>
                ) : (
                  <p style={{ margin: '3px 0' }}>Delivery Charge: +৳ {formatTk(viewInvoice.deliveryCharge)}</p>
                )
              )}
              <h3 style={{ margin:'6px 0', fontSize: '14px' }}>Grand Total: ৳ {formatTk(viewInvoice.totalBill)}</h3>
              <p style={{ margin: '3px 0' }}>{isCourierCod(viewInvoice) ? 'Advance Paid' : 'Paid'}: ৳ {formatTk(viewInvoice.paid)}</p>
              {Number(viewInvoice.dueAmount || 0) > 0 ? (
                isCourierCod(viewInvoice) ? (
                  <p style={{ margin: '3px 0', color: '#1e40af', fontWeight: 'bold' }}>Cash on Delivery: ৳ {formatTk(viewInvoice.dueAmount)}</p>
                ) : (
                  <p style={{ margin: '3px 0', color: '#e74c3c', fontWeight: 'bold' }}>Due: ৳ {formatTk(viewInvoice.dueAmount)}</p>
                )
              ) : (
                <p style={{ margin: '3px 0', color: '#10b981', fontWeight: 'bold' }}>Fully Paid</p>
              )}
            </div>

            <button
              onClick={() => printSalesInvoice(viewInvoice)}
              className="btn-confirm"
              style={{ width: '100%', marginTop: '15px', padding: '10px', fontSize: '13px' }}
            >
              Print Invoice 🖨️
            </button>

            {paymentHistory.length > 0 && (
              <div style={{ marginTop:'12px', paddingTop:'10px', borderTop:'1px dashed #000' }}>
                <p style={{ margin: '0 0 6px 0', fontWeight: 'bold', fontSize: '12px', color: '#4a00e0' }}>Payment History</p>
                {paymentHistory.map((p, i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', color:'#555', marginBottom:'3px' }}>
                    <span>{formatDateSafe(p.createdAt)}</span>
                    <span style={{ color:'#10b981', fontWeight:'bold' }}>+৳ {formatTk(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            {Number(viewInvoice.dueAmount || 0) > 0 && (
              <div style={{ marginTop:'15px', paddingTop:'15px', borderTop:'1px dashed #000', display:'flex', gap:'8px', alignItems:'center' }}>
                <span style={{ fontWeight:'bold', fontSize:'13px' }}>Receive:</span>
                <input
                  type="number"
                  value={receiveInput}
                  onFocus={e => e.target.select()}
                  onChange={e => setReceiveInput(e.target.value)}
                  style={{ flex:1, padding:'8px', borderRadius:'8px', border:'1px solid #ccc', fontSize:'13px' }}
                />
                <button onClick={submitReceive} style={{ padding:'8px 16px', background:'#4a00e0', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'bold', fontSize:'13px' }}>Receive</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
